import React from 'react';
import { Trophy, Medal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';

const Scoreboard = ({ gameState }) => {
  // Sort teams by score (descending)
  const sortedTeams = [...gameState.teams].sort((a, b) => b.score - a.score);
  const maxScore = Math.max(...gameState.teams.map(t => t.score), 1);
  const currentTeamName = gameState.teams[gameState.current_team_index].name;

  return (
    <Card className="sticky top-24" data-testid="scoreboard-card">
      <CardHeader className="bg-gradient-to-r from-[hsl(var(--accent))] to-[hsl(var(--secondary))] text-white border-b">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5" />
          <CardTitle className="text-xl font-[var(--font-display)]">
            Live Scoreboard
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          {sortedTeams.map((team, index) => {
            const isCurrentTeam = team.name === currentTeamName;
            const progressPercent = maxScore > 0 ? (Math.max(team.score, 0) / maxScore) * 100 : 0;

            return (
              <div
                key={team.name}
                className={`p-4 rounded-lg border-2 transition-all ${
                  isCurrentTeam
                    ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5 shadow-md'
                    : 'border-[hsl(var(--border))] bg-white'
                }`}
                data-testid={`team-score-${team.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {index === 0 && (
                      <Medal className="h-5 w-5 text-[hsl(var(--secondary))]" />
                    )}
                    <h3 className="font-[var(--font-display)] text-lg">
                      {team.name}
                    </h3>
                    {isCurrentTeam && (
                      <Badge className="bg-[hsl(var(--accent))] text-white">
                        Playing
                      </Badge>
                    )}
                  </div>
                  <div className="text-right">
                    <div className={`text-3xl font-[var(--font-display)] ${
                      team.score < 0 ? 'text-[hsl(var(--destructive))]' : 'text-[hsl(var(--accent))]'
                    }`}>
                      {team.score}
                    </div>
                    <div className="text-xs text-muted-foreground font-[var(--font-mono)]">
                      points
                    </div>
                  </div>
                </div>
                <Progress value={progressPercent} className="h-2" />
              </div>
            );
          })}
        </div>

        {/* Game stats */}
        <div className="mt-6 pt-6 border-t">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-2xl font-[var(--font-display)] text-[hsl(var(--accent))]">
                {gameState.score_history.length}
              </div>
              <div className="text-xs text-muted-foreground">
                Total Scores
              </div>
            </div>
            <div>
              <div className="text-2xl font-[var(--font-display)] text-[hsl(var(--secondary))]">
                {gameState.teams.length}
              </div>
              <div className="text-xs text-muted-foreground">
                Teams Playing
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Scoreboard;
